import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Availability } from '../components/realTimeLocation';
import { siteLookupTokens } from '../siteLookupTokens';

const { color, size } = siteLookupTokens;

const tones = {
  neutral: { backgroundColor: color.tag, color: color.text.secondary },
  warning: { backgroundColor: color.availability.busy.background, color: color.availability.busy.text },
} as const;

type Props = {
  children: ReactNode;
  tone?: keyof typeof tones;
};

/** A small, non-interactive tag of a station row or sheet. */
const StationTag = ({ children, tone = 'neutral' }: Props) => (
  <Box
    component="span"
    sx={{
      ...tones[tone],
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      borderRadius: '4px',
      fontSize: size.tagFontSize,
      fontWeight: 500,
      lineHeight: '18px',
      paddingX: 0.75,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

/** "● 9/12 available": green by default, orange under 30 %, red when none is. The text always tells the numbers. */
export const AvailabilityTag = ({ availability: { available, level, total } }: { availability: Availability }) => {
  const { t } = useTranslation();
  const { background, text } = color.availability[level];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        borderRadius: '4px',
        backgroundColor: background,
        color: text,
        fontSize: size.tagFontSize,
        fontWeight: 600,
        lineHeight: '18px',
        paddingX: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'currentColor' }} />
      {t('siteLookup.list.available', { available, total })}
    </Box>
  );
};

export default StationTag;
